import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography, Grid, Stack, IconButton, Button, Card, CardContent, Link,
  TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Menu, MenuItem, ListItemIcon, useMediaQuery
} from '@mui/material';
import { Delete, Edit, Inventory2, MoreVert } from '@mui/icons-material';
import { useOrganizationContext } from '../../../context/organization_context/OrganizationContext';
import { ToastType, useToast } from '../../../context/toast/ToastContext';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import ConfirmDeleteModal from '../../../components/modals/ConfirmDeleteModal';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';
import EditInventoryOrderModal from './modals/EditInventoryOrderModal';
import ReceiveOrderModal from './modals/ReceiveOrderModal';
import { deleteInventoryOrder, inventoryOrderSnapshotListener, updateInventoryOrder, fetchOrderHistory } from '../../../repository/OrdersDAO';
import { useInventoryContext } from '../../../context/inventory_context/InventoryContext';
import { Loading } from '../../../components/loading/Loading';
import CustomPagination from '../../../components/pagination/CustomPagination';
import { useLoadingContext } from '../../../context/loading/LoadingContext';
import { Modules } from '../../../utils/permissions/Modules';
import usePermissions from '../../../utils/permissions/UsePermissions';
import { PermissionTypes } from '../../../utils/permissions/PermissionTypes';

const InventoryOrderDetail = () => {

  const { orderId } = useParams();
  const [order, setOrder] = useState();
  const [loading, setLoading] = useState(true);
  const [orderHistory, setOrderHistory] = useState([]);
  const [page, setPage] = useState(0); 
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const { organization, getEmployeeByID } = useOrganizationContext();
  const navigate = useNavigate();
  const { setLoadingData } = useLoadingContext();
  const { showToast } = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showEditOrderModal, setShowEditOrderModal] = useState(false);
  const [showReceiveOrderModal, setShowReceiveOrderModal] = useState(false);
  const { t } = useTranslation();
  const { inventoryItems } = useInventoryContext();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [anchorEl, setAnchorEl] = useState(null);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (organization && orderId) {
      const unsubscribeInventoryOrder = inventoryOrderSnapshotListener(organization.id, orderId, setOrder);
      const unsubscribeOrderHistory = fetchOrderHistory(organization.id, orderId, (historyData) => {
        const sortedHistory = historyData.sort((a, b) => b.update_date.seconds - a.update_date.seconds);
        setOrderHistory(sortedHistory);
      });
      setLoading(false);
      return () => {
        unsubscribeInventoryOrder();
        unsubscribeOrderHistory();
      };
    }
  }, [orderId, organization]);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleDeleteOrder = async () => {
    setLoadingData(true);

    try {
        const result = await deleteInventoryOrder(organization.id, order.id);
        if (result.success) {
            showToast(t(StringKeys.SUCCESSFULLY_DELETED_ORDER), ToastType.SUCCESS);
            navigate('/inventory-orders');
        } else {
            showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
        }
    } catch (error) {
        showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
      } finally {
        setLoadingData(false);
    }
};


  const handleReceiveOrder = async (receivedItems) => {
    setLoadingData(true)

    try {
        for (const item of receivedItems) {
            const originalItem = order.items[item.inventory_item_id];
            const newQuantity = originalItem.quantity_received + item.quantity_received;
            order.items[item.inventory_item_id] = {
                ...originalItem,
                quantity_received: newQuantity,
            };
        }

        const isFullyReceived = Object.values(order.items).every(
            (item) => item.quantity_received === item.quantity_ordered
        );
        const newStatus = isFullyReceived ? 'delivered' : 'partially_received';
        const result = await updateInventoryOrder(organization.id, order.id, { status: newStatus, items: order.items });
        if (result.success) {
            showToast(
                isFullyReceived 
                    ? t(StringKeys.ORDER_FULLY_RECEIVED)
                    : t(StringKeys.ORDER_PARTIALLY_RECEIVED),
                ToastType.SUCCESS
            );
        } else {
            showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
        }
    } catch (error) {
        showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
      } finally {
        setLoadingData(false);
    }
};


  const getItemNameById = (itemId) => {
    const item = inventoryItems.find((i) => i.id === itemId);
    return item ? item.name : t(StringKeys.UNKNOWN);
  };

  const areAllItemsReceived = () => {
    return Object.values(order.items).every(item => item.quantity_received === item.quantity_ordered);
  };

  const formatHistoryItemUpdate = (item) => {
    const action = item.quantity_received > 0 ? "received" : "removed";
    return `${item.quantity_received} units ${action}.`;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedHistory = orderHistory.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading || !order) return <AppSidebar content={<Loading />} />;

  return (
    <AppSidebar
      content={
        <Stack gap={4}>
          <Grid container alignItems="center" spacing={0}>
            {!isMobile && (
              <Grid item xs>
                <Stack direction="row" spacing={1}>
                  {hasPermission(Modules.INVENTORY_ORDERS, PermissionTypes.WRITE) && (
                    <>
                      <Button
                        variant="text"
                        startIcon={<Inventory2 />}
                        onClick={() => setShowReceiveOrderModal(true)}
                        disabled={areAllItemsReceived()}
                        sx={{ textTransform: 'none', fontSize: '1rem' }}
                      >
                        {t(StringKeys.RECEIVE_ORDER)}
                      </Button>
                      <Button
                        variant="text"
                        startIcon={<Delete />}
                        onClick={() => setConfirmOpen(true)}
                        sx={{ textTransform: 'none', fontSize: '1rem' }}
                      >
                        {t(StringKeys.DELETE)}
                      </Button>
                    </>
                  )}
                </Stack>
              </Grid>
            )}
            {isMobile && (
              <Grid item xs="auto">
                <IconButton onClick={handleMenuClick}>
                  <MoreVert />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  {hasPermission(Modules.INVENTORY_ORDERS, PermissionTypes.WRITE) && (
                    <>
                      <MenuItem onClick={() => { setShowReceiveOrderModal(true); handleMenuClose(); }}>
                        <ListItemIcon>
                          <Inventory2 fontSize="small" />
                        </ListItemIcon>
                        {t(StringKeys.RECEIVE_ORDER)}
                      </MenuItem>
                      <MenuItem onClick={() => { setConfirmOpen(true); handleMenuClose(); }}>
                        <ListItemIcon>
                          <Delete fontSize="small" />
                        </ListItemIcon>
                        {t(StringKeys.DELETE)}
                      </MenuItem>
                    </>
                  )}
                </Menu>
              </Grid>
            )}
          </Grid>

          <Card variant="outlined">
            <CardContent>
              <Typography variant={isMobile ? "h6" : "h5"} component="div">{t(StringKeys.ORDER)} #{order.id}</Typography>
              <Typography>{t(StringKeys.SUPPLIER)}: {order.supplier}</Typography>
              <Typography>{t(StringKeys.PURCHASE_ORDER)}: {order.purchase_order || "N/A"}</Typography>
              <Typography>{t(StringKeys.TOTAL_COST)}: ${order.total_cost.toFixed(2)}</Typography>
              <Typography>{t(StringKeys.DATE_CREATED)}: {new Date(order.order_date.seconds * 1000).toLocaleString()}</Typography>
              <Typography>
                  {t(StringKeys.CREATED_BY)}:{' '}
                  {(() => {
                    const employee = getEmployeeByID(order.created_by);
                    if (!employee) return t(StringKeys.UNKNOWN);
                    
                    return hasPermission(Modules.EMPLOYEES, PermissionTypes.READ) ? (
                        <Link
                            onClick={() => navigate(`/employees/${order.created_by}`)}
                            sx={{ cursor: 'pointer' }}
                        >
                            {employee.first_name} {employee.last_name}
                        </Link>
                    ) : (
                        `${employee.first_name} ${employee.last_name}`
                    );
                  })()}
              </Typography>  
              <Typography>{t(StringKeys.EXPECTED_DELIVERY_DATE)}: {new Date(order.expected_delivery_date.seconds * 1000).toLocaleString()}</Typography>
              <Typography>{t(StringKeys.NOTES)}: {order.notes || "N/A"}</Typography>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant={isMobile ? "h6" : "h5"} component="div">{t(StringKeys.ITEMS_ORDERED)}</Typography>
              <TableContainer>
                <Table sx={{minWidth:650}}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t(StringKeys.ITEM_NAME)}</TableCell>
                      <TableCell>{t(StringKeys.PRICE_PER_UNIT)}</TableCell>
                      <TableCell>{t(StringKeys.QUANTITY_ORDERED)}</TableCell>
                      <TableCell>{t(StringKeys.QUANTITY_RECEIVED)}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.keys(order.items).map((itemId) => {
                      const item = order.items[itemId];
                      return (
                        <TableRow key={itemId}>
                          <TableCell>
                            {(() => {
                                const itemName = getItemNameById(itemId);
                                return hasPermission(Modules.INVENTORY, PermissionTypes.READ) ? (
                                    <Link
                                        onClick={() => navigate(`/inventory/${itemId}`)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        {itemName}
                                    </Link>
                                ) : (
                                    itemName
                                );
                            })()}
                          </TableCell>
                          <TableCell>${parseFloat(item.price_per_unit).toFixed(2)}</TableCell>
                          <TableCell>{item.quantity_ordered}</TableCell>
                          <TableCell>{item.quantity_received || 0}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant={isMobile ? "h6" : "h5"} component="div">{t(StringKeys.ORDER_HISTORY)}</Typography>
              {orderHistory.length === 0 ? (
                  <TableContainer>
                    <Table aria-label="no-notes-table">
                        <TableBody>
                            <TableRow>
                                <TableCell colSpan={3} align="center">
                                    <Typography variant="body2">{t(StringKeys.NO_HISTORY)}</Typography>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
              ) : (
                <>
                  <TableContainer>
                    <Table sx={{minWidth:750}}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t(StringKeys.DATE)}</TableCell>
                          <TableCell>{t(StringKeys.UPDATED_BY)}</TableCell>
                          <TableCell>{t(StringKeys.ITEMS_UPDATED)}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedHistory.map((history, index) => (
                          <TableRow key={index}>
                            <TableCell>{new Date(history.update_date.seconds * 1000).toLocaleString()}</TableCell>
                            <TableCell>
                             {(() => {
                                const employee = getEmployeeByID(history.updated_by);
                                if (!employee) {
                                    return t(StringKeys.UNKNOWN);
                                }

                                return hasPermission(Modules.EMPLOYEES, PermissionTypes.READ) ? (
                                    <Link
                                        onClick={() => navigate(`/employees/${history.updated_by}`)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        {employee.first_name} {employee.last_name}
                                    </Link>
                                ) : (
                                    `${employee.first_name} ${employee.last_name}`
                                );
                            })()}
                            </TableCell>
                            <TableCell>
                              <TableContainer>
                                <Table size="small" aria-label="items-updated-table">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>{t(StringKeys.ITEM_NAME)}</TableCell>
                                      <TableCell>{t(StringKeys.RECEIVED_QUANTITY)}</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {history.items_updated.map((item, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell>
                                          {(() => {
                                              const itemName = getItemNameById(item.item_id);
                                              return hasPermission(Modules.INVENTORY, PermissionTypes.READ) ? (
                                                  <Link
                                                      onClick={() => navigate(`/inventory/${item.item_id}`)}
                                                      sx={{ cursor: 'pointer' }}
                                                  >
                                                      {itemName}
                                                  </Link>
                                              ) : (
                                                  itemName
                                              );
                                          })()}
                                        </TableCell>
                                        <TableCell>
                                          {formatHistoryItemUpdate(item)}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <CustomPagination
                    rowsPerPageOptions={[ 10, 25, 100, { label: 'All', value: -1 }]}
                    component="div"
                    count={orderHistory.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </>
              )}
            </CardContent>
          </Card>

          <EditInventoryOrderModal
            show={showEditOrderModal}
            onHide={() => setShowEditOrderModal(false)}
            order={order}
            inventoryItems={inventoryItems}
          />
          <ReceiveOrderModal
            show={showReceiveOrderModal}
            onHide={() => setShowReceiveOrderModal(false)}
            order={order}
            inventoryItems={inventoryItems}
            onReceive={handleReceiveOrder}
          />
          <ConfirmDeleteModal
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={handleDeleteOrder}
            item={t(StringKeys.ORDER)}
          />
        </Stack>
      }
    />
  );
};

export default InventoryOrderDetail;
